import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { useProfile } from '@/hooks/useProfile';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';

const RECOMMENDED_COURSE_OPTIONS = ["Private Class", "Premiere Class", "Premiere Class Inter"];
import { useToast } from '@/hooks/use-toast';
import { ArrowLeft, Loader2, Save } from 'lucide-react';

export default function TeacherTrialEvalPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { profile } = useProfile();
  const { toast } = useToast();
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Read-only info
  const [studentNickname, setStudentNickname] = useState('');
  const [studentName, setStudentName] = useState('');
  const [grade, setGrade] = useState('');
  const [subject, setSubject] = useState('');
  const [classDate, setClassDate] = useState('');
  const [school, setSchool] = useState('');
  const [language, setLanguage] = useState('');
  const [classroomType, setClassroomType] = useState('');
  const [learningFormat, setLearningFormat] = useState('');
  const [otherSubject, setOtherSubject] = useState('');
  const [classType, setClassType] = useState('');
  const [location, setLocation] = useState('');
  const [hours, setHours] = useState<number | null>(null);
  const [trialDay, setTrialDay] = useState('');
  const [trialTime, setTrialTime] = useState('');
  const [desiredStartDate, setDesiredStartDate] = useState('');
  const [convenientTime, setConvenientTime] = useState('');
  const [inconvenientTime, setInconvenientTime] = useState('');
  const [inconvenientReason, setInconvenientReason] = useState('');
  const [parentName, setParentName] = useState('');
  const [teachingStyle, setTeachingStyle] = useState<string[]>([]);
  const [parentConcerns, setParentConcerns] = useState('');
  const [parentExpectationsTrial, setParentExpectationsTrial] = useState('');
  const [parentExpectationsFishsix, setParentExpectationsFishsix] = useState('');
  const [successCriteria, setSuccessCriteria] = useState('');
  const [additionalPainPoints, setAdditionalPainPoints] = useState('');

  // Editable Section 5
  const [preScoreReason, setPreScoreReason] = useState('');
  const [postScoreReason, setPostScoreReason] = useState('');
  const [trialOverview, setTrialOverview] = useState('');
  const [studentStrengths, setStudentStrengths] = useState('');
  const [studentImprovements, setStudentImprovements] = useState('');
  const [teachingApproach, setTeachingApproach] = useState('');
  const [recommendedDuration, setRecommendedDuration] = useState('');
  const [recommendedCourse, setRecommendedCourse] = useState('');
  const [trialTopic, setTrialTopic] = useState('');

  // Acknowledgement
  const [teacherAcknowledged, setTeacherAcknowledged] = useState(false);

  useEffect(() => {
    const fetchTrial = async () => {
      if (!id) return;
      const { data, error } = await supabase
        .from('class_trials')
        .select('*')
        .eq('id', id)
        .single();

      if (error || !data) {
        toast({ title: t('trialEval.notFound'), variant: 'destructive' });
        navigate('/teacher-trials');
        return;
      }

      // Guard: ensure this trial belongs to the logged-in teacher
      if (profile?.teacher_id && data.teacher_code && data.teacher_code !== profile.teacher_id) {
        toast({ title: t('trialEval.notFound'), variant: 'destructive' });
        navigate('/teacher-trials');
        return;
      }

      // Populate read-only
      setStudentNickname(data.student_nickname || '');
      setStudentName(data.student_name || '');
      setGrade(data.grade || '');
      setSubject(data.subject || '');
      setClassDate(data.class_date || '');
      const d = data as any;
      setSchool(d.school || '');
      setLanguage(d.language || '');
      setClassroomType(d.classroom_type || '');
      setLearningFormat(d.learning_format || '');
      setOtherSubject(d.other_subject || '');
      setClassType(d.class_type || '');
      setLocation(d.location || '');
      setHours(d.hours ?? null);
      const trialTimeFull: string = d.trial_time || '';
      const dateMatch = trialTimeFull.match(/^(\d{4}-\d{2}-\d{2})\s*(.*)?$/);
      if (dateMatch) {
        setTrialDay(dateMatch[1]);
        setTrialTime(dateMatch[2] || '');
      } else {
        setTrialTime(trialTimeFull);
      }
      setDesiredStartDate(d.desired_start_date || '');
      setConvenientTime(d.convenient_time || '');
      setInconvenientTime(d.inconvenient_time || '');
      setInconvenientReason(d.inconvenient_reason || '');
      setParentName(d.parent_name || '');
      setTeachingStyle(Array.isArray(d.teaching_style) ? d.teaching_style : []);
      const rawConcerns: string = d.parent_concerns || '';
      try {
        const parsed = JSON.parse(rawConcerns);
        setParentConcerns(Array.isArray(parsed) ? parsed.join(', ') : rawConcerns);
      } catch {
        setParentConcerns(rawConcerns);
      }
      setParentExpectationsTrial(d.parent_expectations_trial || '');
      setParentExpectationsFishsix(d.parent_expectations_fishsix || '');
      setSuccessCriteria(d.success_criteria || '');
      setAdditionalPainPoints(d.additional_pain_points || '');

      // Populate editable
      setPreScoreReason(data.pre_score_reason || '');
      setPostScoreReason(data.post_score_reason || '');
      setTrialOverview(data.trial_overview || '');
      setStudentStrengths(data.student_strengths || '');
      setStudentImprovements(data.student_improvements || '');
      setTeachingApproach(data.teaching_approach || '');
      setRecommendedDuration(data.recommended_duration || '');
      setRecommendedCourse(data.recommended_course || '');
      setTrialTopic(data.trial_topic || '');
      setTeacherAcknowledged(data.teacher_acknowledged || false);

      setIsLoading(false);
    };
    fetchTrial();
  }, [id, navigate, toast, profile?.teacher_id]);

  const handleSave = async () => {
    if (!id) return;
    setIsSaving(true);

    const { error } = await supabase
      .from('class_trials')
      .update({
        pre_score_reason: preScoreReason || null,
        post_score_reason: postScoreReason || null,
        trial_overview: trialOverview || null,
        student_strengths: studentStrengths || null,
        student_improvements: studentImprovements || null,
        teaching_approach: teachingApproach || null,
        recommended_duration: recommendedDuration || null,
        recommended_course: recommendedCourse || null,
        trial_topic: trialTopic || null,
        teacher_acknowledged: teacherAcknowledged,
      })
      .eq('id', id);

    setIsSaving(false);

    if (error) {
      toast({ title: t('trialEval.saveError'), description: error.message, variant: 'destructive' });
    } else {
      toast({ title: t('trialEval.saveSuccess') });
      navigate('/teacher-trials');
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6 max-w-3xl space-y-6">
      <Button variant="ghost" size="sm" onClick={() => navigate('/teacher-trials')}>
        <ArrowLeft className="h-4 w-4 mr-1" /> {t('trialEval.back')}
      </Button>

      {/* Student Info (read-only) */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{t('trialEval.studentInfo')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-sm">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <div><span className="text-muted-foreground">{t('trialEval.nickname')}:</span> {studentNickname || '-'}</div>
            <div><span className="text-muted-foreground">{t('trialEval.fullName')}:</span> {studentName || '-'}</div>
            <div><span className="text-muted-foreground">{t('trialEval.grade')}:</span> {grade || '-'}</div>
            <div><span className="text-muted-foreground">โรงเรียน:</span> {school || '-'}</div>
            <div><span className="text-muted-foreground">{t('trialEval.subject')}:</span> {subject || '-'}</div>
            {otherSubject && <div><span className="text-muted-foreground">วิชาอื่น:</span> {otherSubject}</div>}
            <div><span className="text-muted-foreground">ภาษา:</span> {language || '-'}</div>
            <div><span className="text-muted-foreground">ประเภทห้องเรียน:</span> {classroomType || '-'}</div>
            <div><span className="text-muted-foreground">รูปแบบการเรียน:</span> {learningFormat || '-'}</div>
            <div><span className="text-muted-foreground">ประเภทเรียน:</span> {classType || '-'}</div>
            <div><span className="text-muted-foreground">สถานที่:</span> {location || '-'}</div>
            <div><span className="text-muted-foreground">จำนวนชั่วโมง:</span> {hours ?? '-'}</div>
            <div><span className="text-muted-foreground">{t('trialEval.classDate')}:</span> {classDate || '-'}</div>
            <div><span className="text-muted-foreground">วันทดลอง:</span> {trialDay || '-'}</div>
            <div><span className="text-muted-foreground">เวลาทดลอง:</span> {trialTime || '-'}</div>
            <div><span className="text-muted-foreground">วันที่ต้องการเริ่มเรียนจริง:</span> {desiredStartDate || '-'}</div>
            <div className="col-span-2 sm:col-span-3"><span className="text-muted-foreground">วัน/เวลาที่สะดวก:</span> {convenientTime || '-'}</div>
            <div className="col-span-2 sm:col-span-3"><span className="text-muted-foreground">ช่วงเวลาที่ไม่สะดวก:</span> {inconvenientTime || '-'}</div>
            <div className="col-span-2 sm:col-span-3"><span className="text-muted-foreground">เหตุผลที่ไม่สะดวก:</span> {inconvenientReason || '-'}</div>
            <div><span className="text-muted-foreground">ชื่อผู้ปกครอง:</span> {parentName || '-'}</div>
          </div>
          <div className="border-t pt-4 space-y-2">
            <div><span className="text-muted-foreground">สไตล์การสอนที่ต้องการ:</span> {teachingStyle.length ? teachingStyle.join(', ') : '-'}</div>
            <div><span className="text-muted-foreground">ความกังวลของผู้ปกครอง:</span> {parentConcerns || '-'}</div>
            <div><span className="text-muted-foreground">ความคาดหวังจากการทดลอง:</span> {parentExpectationsTrial || '-'}</div>
            <div><span className="text-muted-foreground">ความคาดหวังจาก Fishsix:</span> {parentExpectationsFishsix || '-'}</div>
            <div><span className="text-muted-foreground">เกณฑ์ความสำเร็จ:</span> {successCriteria || '-'}</div>
            <div><span className="text-muted-foreground">Pain points เพิ่มเติม:</span> {additionalPainPoints || '-'}</div>
          </div>
        </CardContent>
      </Card>

      {/* Teacher Acknowledgement */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{t('trialEval.ackTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-sm">
          <div className="space-y-3 leading-relaxed">
            <p>{t('trialEval.ack.intro1')}</p>
            <p>{t('trialEval.ack.intro2')}</p>
            <p>{t('trialEval.ack.intro3')}</p>
          </div>

          <div className="space-y-3 leading-relaxed border-t pt-4">
            <h3 className="font-semibold">{t('trialEval.ack.prepTitle')}</h3>
            <p><strong>{t('trialEval.ack.prep1Title')}</strong><br />{t('trialEval.ack.prep1')}</p>
            <p><strong>{t('trialEval.ack.prep2Title')}</strong><br />{t('trialEval.ack.prep2')}</p>
            <p><strong>{t('trialEval.ack.prep3Title')}</strong><br />{t('trialEval.ack.prep3')}</p>
          </div>

          <div className="space-y-3 leading-relaxed border-t pt-4">
            <h3 className="font-semibold">{t('trialEval.ack.duringTitle')}</h3>
            <p><strong>{t('trialEval.ack.during1Title')}</strong><br />{t('trialEval.ack.during1')}</p>
            <p><strong>{t('trialEval.ack.during2Title')}</strong><br />{t('trialEval.ack.during2')}</p>
            <p><strong>{t('trialEval.ack.during3Title')}</strong><br />{t('trialEval.ack.during3')}</p>
            <p><strong>{t('trialEval.ack.during4Title')}</strong><br />{t('trialEval.ack.during4')}</p>
            <p><strong>{t('trialEval.ack.during5Title')}</strong><br />{t('trialEval.ack.during5')}</p>
            <p><strong>{t('trialEval.ack.during6Title')}</strong><br />{t('trialEval.ack.during6')}</p>
          </div>

          <div className="flex items-start gap-2 border-t pt-4">
            <Checkbox
              id="teacher-acknowledged"
              checked={teacherAcknowledged}
              onCheckedChange={(v) => setTeacherAcknowledged(v === true)}
            />
            <Label htmlFor="teacher-acknowledged" className="cursor-pointer leading-relaxed">
              {t('trialEval.ackCheckbox')}
            </Label>
          </div>
        </CardContent>
      </Card>

      {/* Section 5: Teacher Evaluation */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{t('trialEval.section5Title')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>{t('trialEval.trialTopic')}</Label>
            <Input value={trialTopic} onChange={(e) => setTrialTopic(e.target.value)} placeholder={t('trialEval.trialTopicPlaceholder')} />
          </div>
          <div className="space-y-2">
            <Label>{t('trialEval.preScore')}</Label>
            <Textarea value={preScoreReason} onChange={(e) => setPreScoreReason(e.target.value)} rows={2} placeholder={t('trialEval.preScorePlaceholder')} />
          </div>
          <div className="space-y-2">
            <Label>{t('trialEval.postScore')}</Label>
            <p className="text-xs text-muted-foreground">{t('trialEval.postScoreHint')}</p>
            <Textarea value={postScoreReason} onChange={(e) => setPostScoreReason(e.target.value)} rows={2} placeholder={t('trialEval.postScorePlaceholder')} />
          </div>
          <div className="space-y-2">
            <Label>{t('trialEval.overview')}</Label>
            <Textarea value={trialOverview} onChange={(e) => setTrialOverview(e.target.value)} rows={3} placeholder={t('trialEval.overviewPlaceholder')} />
          </div>
          <div className="space-y-2">
            <Label>{t('trialEval.strengths')}</Label>
            <Textarea value={studentStrengths} onChange={(e) => setStudentStrengths(e.target.value)} rows={2} placeholder={t('trialEval.strengthsPlaceholder')} />
          </div>
          <div className="space-y-2">
            <Label>{t('trialEval.improvements')}</Label>
            <Textarea value={studentImprovements} onChange={(e) => setStudentImprovements(e.target.value)} rows={2} placeholder={t('trialEval.improvementsPlaceholder')} />
          </div>
          <div className="space-y-2">
            <Label>{t('trialEval.approach')}</Label>
            <Textarea value={teachingApproach} onChange={(e) => setTeachingApproach(e.target.value)} rows={2} placeholder={t('trialEval.approachPlaceholder')} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t('trialEval.duration')}</Label>
              <Input value={recommendedDuration} onChange={(e) => setRecommendedDuration(e.target.value)} placeholder={t('trialEval.durationPlaceholder')} />
            </div>
            <div className="space-y-2">
              <Label>{t('trialEval.course')}</Label>
              <p className="text-xs text-muted-foreground">{t('trialEval.courseHint')}</p>
              <Select value={recommendedCourse} onValueChange={setRecommendedCourse}>
                <SelectTrigger><SelectValue placeholder={t('trialEval.selectCourse')} /></SelectTrigger>
                <SelectContent>
                  {RECOMMENDED_COURSE_OPTIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end gap-3 pb-8">
        <Button variant="outline" onClick={() => navigate('/teacher-trials')}>{t('trialEval.cancel')}</Button>
        <Button onClick={handleSave} disabled={isSaving}>
          {isSaving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
          {t('trialEval.save')}
        </Button>
      </div>
    </div>
  );
}
